import type React from 'react';
import { Package2, Printer, Film, Flame, Scan, Archive, Boxes, FolderArchive, PackageOpen, Layers, Activity } from 'lucide-react';

export interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  check?: () => boolean;
  badge?: number;
  indicator?: boolean;
  /** Onboarding-style progress: renders "done/total" + a thin brand-purple
   *  bar under the label — a temporary companion counting down to its own
   *  disappearance, not a permanent menu item. */
  progress?: { done: number; total: number };
}

export interface StationNavInput {
  role: string | null | undefined;
  /** Org has the DTF line on (or the answer is not in yet; see AppSidebar). */
  dtfVisible: boolean;
  can: (permission: 'tracker.view') => boolean;
  t: (key: string) => string;
  showPickerIndicator?: boolean;
}

/**
 * The station sidebar: each station role's own stations plus the tracker.
 *
 * BLU-653: the DTF/film entries for Printer / Catcher / Packer gate on
 * dtfVisible, so an org that turns DTF off loses them here too. Pretreat has
 * no station entry at all.
 *
 * DTF Printer gets exactly its three film pages, NOT gated on dtfVisible: the
 * film pages are its whole job, and its sidebar never reads station settings
 * (getStationSettings is not on its procedure allowlist). The tracker item is
 * hidden for it by its check (no tracker.view); every other station role has
 * tracker.view, so their lists are unchanged.
 */
export function buildStationNavItems({ role, dtfVisible, can, t, showPickerIndicator }: StationNavInput): NavItem[] {
  return [
    ...(role === 'Picker'
      ? [{ id: 'prod-picker', path: '/production/picker', label: t('production.picker'), icon: Package2, indicator: showPickerIndicator }]
      : []),
    ...(role === 'Printer'
      ? [
          { id: 'prod-print', path: '/production/printer', label: t('production.printer'), icon: Printer },
          // Phase 50.01.3 / BLU-233: Printer role runs the DTF film station too.
          ...(dtfVisible ? [{ id: 'prod-film-print', path: '/production/film-printing', label: t('production.filmPrinting'), icon: Film } as NavItem] : []),
        ]
      : []),
    ...(role === 'Catcher'
      ? [
          // DTF Matching station — the Catcher did this match inline at press before.
          ...(dtfVisible ? [{ id: 'prod-matching', path: '/production/matching', label: t('production.matching'), icon: Layers } as NavItem] : []),
          // Phase 50.01.4 / BLU-233: Catcher role runs the DTF press station too.
          ...(dtfVisible ? [{ id: 'prod-pressing', path: '/production/pressing', label: t('production.pressing'), icon: Flame } as NavItem] : []),
          { id: 'prod-catcher', path: '/production/catcher', label: t('production.catcher'), icon: Scan },
        ]
      : []),
    ...(role === 'Packer'
      ? [
          { id: 'prod-packing', path: '/production/packer', label: t('production.packer'), icon: PackageOpen },
          { id: 'prod-bins', path: '/production/bins', label: t('production.bins'), icon: Archive },
          // Phase 50.01.4 / BLU-233: Packer role manages DTF film storage (mirrors bins).
          ...(dtfVisible ? [{ id: 'prod-film-storage', path: '/production/film-storage', label: t('production.filmStorage'), icon: FolderArchive } as NavItem] : []),
          // DTF Matching station — the Packer stands at the pigeon-holes.
          ...(dtfVisible ? [{ id: 'prod-matching', path: '/production/matching', label: t('production.matching'), icon: Layers } as NavItem] : []),
        ]
      : []),
    ...(role === 'DTF Printer'
      ? [
          { id: 'prod-film-print', path: '/production/film-printing', label: t('production.filmPrinting'), icon: Film },
          { id: 'prod-film-preprint', path: '/production/film-preprint', label: t('production.filmPreprint'), icon: Boxes },
          { id: 'prod-film-storage', path: '/production/film-storage', label: t('production.filmStorage'), icon: FolderArchive },
        ]
      : []),
    { id: 'prod-tracker', path: '/production/tracker', label: t('production.tracker'), icon: Activity, check: () => can('tracker.view') },
  ];
}
